interface BindValue {
  symbol: string;
  property: string;
  index: number;
  nestedIndex?: number;
}

interface ElementData {
  tagName: string;
  bind?: BindValue;
  content: string;
  text?: string;
  styles: Record<string, string>;
  source?: {
    fileName: string;
    lineNumber: number;
    columnNumber: number;
  };
  path: string;
  attributes: Record<string, string>;
  className: string;
  children?: Array<Record<string, unknown>>;
  rawJsx?: string;
  iconName?: string;
}

// Message Type Constants
const PreviewMessageType = {
  // Selection messages
  ENABLE_SELECTION: "enable-selection",
  DISABLE_SELECTION: "disable-selection",
  ELEMENT_SELECTED: "element-selected",
  CLEAR_SELECTION: "clear-selection",

  // Element manipulation
  ELEMENT_UPDATED: "element-updated",

  // Resize
  ELEMENT_RESIZED: "element-resized",

  // Component CRUD
  ADD_COMPONENT: "add-component",
  DUPLICATE_ELEMENT: "duplicate-element",
  COMPONENT_ADDED: "component-added",
  COMPONENT_DELETED: "component-deleted",

  // Drag and drop
  DRAG_ENABLED: "drag-enabled",
  DRAG_MOVE: "drag-move",
  DRAG_END: "drag-end",

  // Layer management
  GET_LAYERS: "get-layers",
  LAYER_ANALYSIS: "layer-analysis",
  SELECT_ELEMENT_BY_PATH: "select-element-by-path",
  SELECT_PARENT: "select-parent",
  HOVER_ELEMENT_BY_PATH: "hover-element-by-path",
  REORDER_ELEMENTS: "reorder-elements",
  ELEMENTS_REORDERED: "elements-reordered",
  MOVE_ELEMENT: "move-element",

  // Undo/Redo operations
  UNDO_REQUEST: "undo-request",
  REDO_REQUEST: "redo-request",
  COPY_ELEMENT: "copy-element",
  PASTE_ELEMENT: "paste-element",
  UNDO_DELETE: "undo-delete",
  REDO_DELETE: "redo-delete",
  UNDO_ADD: "undo-add",
  REDO_ADD: "redo-add",

  // Content editing
  CONTENT_CHANGED: "content-changed",

  // Cleanup
  CLEANUP_TEMP_ELEMENTS: "cleanup-temp-elements",
  RELOAD_IFRAME: "reload-iframe",
  UNDO_DOM_MOVES: "undo-dom-moves",

  // Navigation
  ROUTE_CHANGED: "route-changed",
  NAVIGATE_TO_ROUTE: "navigate-to-route",

  // Error handling (from iframe to parent)
  OPTIDEV_FIX_ERROR: "OPTIDEV_FIX_ERROR",

  // Scroll events (from iframe to parent)
  SCROLL_POSITION: "scroll-position",

  // Theme messages
  INIT_THEME: "init-theme",
  UPDATE_THEME: "update-theme",
  RESET_THEME: "reset-theme",
  SET_THEME_MODE: "set-theme-mode",

  // Script injection
  INJECT_TAILWIND_CDN: "inject-tailwind-cdn",

  // Slide navigation
  SLIDE_NAVIGATE: "slide-navigate",
  PRESENTATION_CONFIG_UPDATE: "presentation-config-update",
  PER_SLIDE_CONFIG_UPDATE: "per-slide-config-update",
} as const;

type PreviewMessageTypeValue =
  (typeof PreviewMessageType)[keyof typeof PreviewMessageType];

// Message Data Interfaces (only used ones)
interface AddComponentMessage {
  type: typeof PreviewMessageType.ADD_COMPONENT;
  component: string;
  id: string;
  props: Record<string, any>;
  "data-source": string;
}

interface DuplicateElementMessage {
  type: typeof PreviewMessageType.DUPLICATE_ELEMENT;
  source: string;
  newId: string;
}

interface DragMoveMessage {
  type: typeof PreviewMessageType.DRAG_MOVE;
  component: string;
  id: string;
  props: Record<string, any>;
  "data-source": string;
}

interface ElementUpdatedMessage {
  type: typeof PreviewMessageType.ELEMENT_UPDATED;
  updatedElement: ElementData;
}

interface SelectElementByPathMessage {
  type: typeof PreviewMessageType.SELECT_ELEMENT_BY_PATH;
  path: string;
}

interface SelectParentMessage {
  type: typeof PreviewMessageType.SELECT_PARENT;
}

interface CleanupTempElementsMessage {
  type: typeof PreviewMessageType.CLEANUP_TEMP_ELEMENTS;
  elementPaths: string[];
  deletedElementPaths?: string[];
}

interface UndoAddMessage {
  type: typeof PreviewMessageType.UNDO_ADD;
  element: ElementData;
  elementPath: string;
  parentId?: string | null;
  position?: number | string | null;
}

interface RedoAddMessage {
  type: typeof PreviewMessageType.REDO_ADD;
  element: ElementData;
  elementPath: string;
  parentId?: string | null;
  position?: number | string | null;
}

interface UndoDeleteMessage {
  type: typeof PreviewMessageType.UNDO_DELETE;
  element: ElementData;
  elementPath: string;
  parentId?: string | null;
  position?: number | string | null;
}

interface RedoDeleteMessage {
  type: typeof PreviewMessageType.REDO_DELETE;
  element: ElementData;
  elementPath: string;
  parentId?: string | null;
  position?: number | string | null;
}

interface MoveElementMessage {
  type: typeof PreviewMessageType.MOVE_ELEMENT;
  elementId: string;
  elementPath: string;
  parentId?: string | null;
  position?: number | string | null;
}

interface HoverElementByPathMessage {
  type: typeof PreviewMessageType.HOVER_ELEMENT_BY_PATH;
  path: string | null;
}

interface ReorderElementsMessage {
  type: typeof PreviewMessageType.REORDER_ELEMENTS;
  from: string;
  to: string;
  oldParentId?: string | null;
  oldPosition?: number | null;
  elementData?: any;
}

interface ElementSelectedMessage {
  type: typeof PreviewMessageType.ELEMENT_SELECTED;
  element: ElementData;
  isAdditive?: boolean;
  selectedPaths?: string[];
}

interface ElementResizedMessage {
  type: typeof PreviewMessageType.ELEMENT_RESIZED;
  element: ElementData;
  width: number;
  height: number;
}

interface ComponentAddedMessage {
  type: typeof PreviewMessageType.COMPONENT_ADDED;
  id: string;
  component: string;
}

interface ComponentDeletedMessage {
  type: typeof PreviewMessageType.COMPONENT_DELETED;
  id: string;
  bulkDeleteId?: string;
}

interface DragEnabledMessage {
  type: typeof PreviewMessageType.DRAG_ENABLED;
}

interface DragEndMessage {
  type: typeof PreviewMessageType.DRAG_END;
  elementId: string;
}

interface LayerAnalysisMessage {
  type: typeof PreviewMessageType.LAYER_ANALYSIS;
  layers: any[];
}

interface ElementsReorderedMessage {
  type: typeof PreviewMessageType.ELEMENTS_REORDERED;
  from: string;
  to: string;
}

interface UndoRequestMessage {
  type: typeof PreviewMessageType.UNDO_REQUEST;
}

interface RedoRequestMessage {
  type: typeof PreviewMessageType.REDO_REQUEST;
}

interface ContentChangedMessage {
  type: typeof PreviewMessageType.CONTENT_CHANGED;
  elementPath: string;
  content: string;
}

interface RouteChangedMessage {
  type: typeof PreviewMessageType.ROUTE_CHANGED;
  path: string;
}

interface NavigateToRouteMessage {
  type: typeof PreviewMessageType.NAVIGATE_TO_ROUTE;
  path: string;
}

interface UndoDomMovesMessage {
  type: typeof PreviewMessageType.UNDO_DOM_MOVES;
  moves: Array<{
    elementPath: string;
    parentBefore: string | null;
    positionBefore: number | string | null;
  }>;
}

/**
 * Error info extracted from Vite error overlay
 */
interface FixErrorInfo {
  message: string;
  file: string | null;
  frame: string | null;
  stack: string | null;
  tip: string | null;
  plugin: string | null;
  timestamp: string;
}

/**
 * Fix error message from iframe to parent
 * Sent when user clicks "Fix Issue" button on Vite error overlay
 */
interface FixErrorMessage {
  type: typeof PreviewMessageType.OPTIDEV_FIX_ERROR;
  error: FixErrorInfo;
  formattedMessage: string;
}

/**
 * Scroll position message from iframe to parent
 * Sent when user scrolls in the iframe
 */
interface ScrollPositionMessage {
  type: typeof PreviewMessageType.SCROLL_POSITION;
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  isNearBottom: boolean; // true if within 100px of bottom
}

/**
 * Update theme message from parent to iframe
 * Sent when user selects a new theme
 */
interface UpdateThemeMessage {
  type: typeof PreviewMessageType.UPDATE_THEME;
  themeId: string;
  content: string;
  googleFonts?: Record<string, string>; // Font name → Google Fonts URL param
}

/**
 * Reset theme message from parent to iframe
 * Sent when user stops hovering a theme (mouse leave)
 */
interface ResetThemeMessage {
  type: typeof PreviewMessageType.RESET_THEME;
}

/**
 * Set theme mode message from parent to iframe
 * Sent when user switches between light/dark mode
 */
interface SetThemeModeMessage {
  type: typeof PreviewMessageType.SET_THEME_MODE;
  mode: "light" | "dark";
}

/**
 * Initialize theme message from parent to iframe
 * Sent once when iframe loads to cache font configuration
 */
interface InitThemeMessage {
  type: typeof PreviewMessageType.INIT_THEME;
  googleFonts: Record<string, string>;
}

/**
 * Navigate to a specific slide index (parent -> iframe)
 */
interface SlideNavigateMessage {
  type: typeof PreviewMessageType.SLIDE_NAVIGATE;
  slideIndex: number;
}

/**
 * Update presentation config in the iframe at runtime (parent -> iframe)
 * Uses Record<string, unknown> because the iframe (separate repo) doesn't share
 * the PresentationConfig type — it parses and validates values at runtime.
 */
interface PresentationConfigUpdateMessage {
  type: typeof PreviewMessageType.PRESENTATION_CONFIG_UPDATE;
  config: Record<string, boolean | number | string>;
}

/**
 * Update per-slide config override in the iframe (parent -> iframe)
 */
interface PerSlideConfigUpdateMessage {
  type: typeof PreviewMessageType.PER_SLIDE_CONFIG_UPDATE;
  slideId: string;
  config: Record<string, boolean | number | string | null>;
}

// Union type for all possible preview messages
type PreviewMessage =
  // Simple messages (no payload)
  | { type: typeof PreviewMessageType.ENABLE_SELECTION }
  | { type: typeof PreviewMessageType.DISABLE_SELECTION }
  | { type: typeof PreviewMessageType.CLEAR_SELECTION }
  | { type: typeof PreviewMessageType.GET_LAYERS }
  | { type: typeof PreviewMessageType.RELOAD_IFRAME }
  // Complex messages with interfaces
  | AddComponentMessage
  | DuplicateElementMessage
  | DragMoveMessage
  | ElementUpdatedMessage
  | SelectElementByPathMessage
  | SelectParentMessage
  | CleanupTempElementsMessage
  | UndoAddMessage
  | RedoAddMessage
  | UndoDeleteMessage
  | RedoDeleteMessage
  | MoveElementMessage
  | HoverElementByPathMessage
  | ReorderElementsMessage
  | ElementSelectedMessage
  | ElementResizedMessage
  | ComponentAddedMessage
  | ComponentDeletedMessage
  | DragEnabledMessage
  | DragEndMessage
  | LayerAnalysisMessage
  | ElementsReorderedMessage
  | UndoRequestMessage
  | RedoRequestMessage
  | ContentChangedMessage
  | RouteChangedMessage
  | NavigateToRouteMessage
  | UndoDomMovesMessage
  | FixErrorMessage
  | ScrollPositionMessage
  | InitThemeMessage
  | UpdateThemeMessage
  | ResetThemeMessage
  | SetThemeModeMessage
  | { type: typeof PreviewMessageType.INJECT_TAILWIND_CDN }
  // Slide messages
  | SlideNavigateMessage
  | PresentationConfigUpdateMessage
  | PerSlideConfigUpdateMessage;

export { PreviewMessageType };

export type {
  PreviewMessageTypeValue,
  AddComponentMessage,
  DragMoveMessage,
  ElementUpdatedMessage,
  SelectElementByPathMessage,
  SelectParentMessage,
  CleanupTempElementsMessage,
  UndoAddMessage,
  RedoAddMessage,
  UndoDeleteMessage,
  RedoDeleteMessage,
  MoveElementMessage,
  HoverElementByPathMessage,
  ReorderElementsMessage,
  ElementSelectedMessage,
  ElementResizedMessage,
  ComponentAddedMessage,
  ComponentDeletedMessage,
  DragEnabledMessage,
  DragEndMessage,
  LayerAnalysisMessage,
  ElementsReorderedMessage,
  UndoRequestMessage,
  RedoRequestMessage,
  ContentChangedMessage,
  RouteChangedMessage,
  NavigateToRouteMessage,
  UndoDomMovesMessage,
  FixErrorInfo,
  FixErrorMessage,
  ScrollPositionMessage,
  InitThemeMessage,
  UpdateThemeMessage,
  ResetThemeMessage,
  SetThemeModeMessage,
  // Slide messages
  SlideNavigateMessage,
  PresentationConfigUpdateMessage,
  PerSlideConfigUpdateMessage,
  // Union type
  PreviewMessage,
  BindValue,
  ElementData,
};
